import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PaperPlaneTilt, Bell, EnvelopeSimple, CheckCircle, Clock, MapPin, CalendarBlank, FolderOpen, ChatCircleText, Info, LinkSimple as Link2 } from "@phosphor-icons/react";
// Iter207.18 — SatisfactionWidget rimosso dal marketplace, ora è dentro PropertyDetail.
import RescheduleVisitDialog from "@/components/RescheduleVisitDialog";
import PracticeCustomActionsPanel from "@/components/PracticeCustomActionsPanel";

const fmt = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("it-IT") : "—");

/** Bottone di azione per una pratica del PRO nello Storico Servizi.
 * - Se in lavorazione → "Sollecita" (apre SolicitDialog)
 * - Se completata + entro 48h lav. → "Richiedi integrazioni" (apre IntegrationDialog)
 * `data` = riga del recap (id, status, completed_at, ...). Compact = variante inline stretta.
 */
export function PracticeActionButton({ practice, compact = false, onSent, onBeforeAction }) {
  const [solicitOpen, setSolicitOpen] = useState(false);
  const [integrationOpen, setIntegrationOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const inProgress = !["completed", "cancelled"].includes(practice.status);
  const completed = practice.status === "completed";
  // Gating "Sollecita": disponibile solo se days_left ≤ 0 (data consegna raggiunta).
  const daysLeft = practice.days_left;
  const canSolicit = daysLeft === null || daysLeft === undefined || daysLeft <= 0;
  // Gating "Cambia data": pratica in corso + can_reschedule_visit dal backend.
  const canReschedule = inProgress && practice.can_reschedule_visit === true;

  // Rate limit 48h sul sollecito: se il PRO ha già sollecitato meno di 48h fa,
  // disabilitiamo il bottone e mostriamo data/ora dell'ultimo invio.
  const lastSolicitAt = practice.last_solicit_at || null;
  const solicitCooldown = (() => {
    if (!lastSolicitAt) return null;
    const last = new Date(lastSolicitAt);
    if (isNaN(last.getTime())) return null;
    const elapsedMs = Date.now() - last.getTime();
    const limitMs = 48 * 3600 * 1000;
    if (elapsedMs >= limitMs) return null;
    return {
      lastDate: last.toLocaleDateString("it-IT"),
      lastTime: last.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }),
    };
  })();

  // 48h lavorative dalla completed_at
  const withinIntegrationWindow = (() => {
    if (!completed || !practice.completed_at) return false;
    const start = new Date(practice.completed_at);
    const now = new Date();
    if (now <= start) return true;
    // Conta ore Lun-Ven (skip Sab/Dom)
    let hours = 0;
    const cur = new Date(start);
    while (cur < now) {
      const next = new Date(cur);
      next.setDate(next.getDate() + 1);
      next.setHours(0, 0, 0, 0);
      const chunkEnd = next > now ? now : next;
      const dow = cur.getDay(); // 0=Dom, 6=Sab
      if (dow !== 0 && dow !== 6) {
        hours += (chunkEnd - cur) / 3600000;
      }
      cur.setTime(chunkEnd.getTime());
    }
    return hours <= 48;
  })();

  if (inProgress) {
    // Sempre disponibile per pratiche in corso con visit riprogrammabile: "Cambia data".
    // Il pulsante "Sollecita" è aggiuntivo (visibile solo se il countdown è esaurito).
    const rescheduleBtn = canReschedule && (
      <>
        <Button
          size={compact ? "sm" : "default"}
          variant="outline"
          className="border-blue-300 text-blue-700 hover:bg-blue-50"
          onClick={(e) => { e.stopPropagation(); onBeforeAction?.(); setRescheduleOpen(true); }}
          data-testid={`reschedule-btn-${practice.id || practice._id}`}
        >
          <CalendarBlank size={compact ? 12 : 14} className="mr-1" weight="bold" /> Cambia data
        </Button>
        <RescheduleVisitDialog
          open={rescheduleOpen}
          onClose={() => setRescheduleOpen(false)}
          practice={practice}
          onDone={onSent}
        />
      </>
    );

    if (!canSolicit) {
      // Consegna ancora nei tempi (days_left > 0). Mostro comunque "Cambia data" se applicabile.
      return (
        <div className="flex items-center gap-2">
          {rescheduleBtn}
          <span
            className="text-[10px] text-slate-400 italic"
            data-testid={`solicit-locked-${practice.id || practice._id}`}
            title={`Il pulsante "Sollecita" sarà disponibile dalla data di consegna stimata (mancano ${daysLeft} gg).`}
          >
            in tempo
          </span>
        </div>
      );
    }
    return (
      <div className="flex items-start gap-2 flex-wrap">
        {rescheduleBtn}
        <div className="flex flex-col items-start gap-1">
          <Button
            size={compact ? "sm" : "default"}
            variant="outline"
            className="border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={(e) => { e.stopPropagation(); onBeforeAction?.(); setSolicitOpen(true); }}
            disabled={!!solicitCooldown}
            title={
              solicitCooldown
                ? `Hai già sollecitato in data ${solicitCooldown.lastDate} alle ore ${solicitCooldown.lastTime}, attendi una risposta`
                : undefined
            }
            data-testid={`solicit-btn-${practice.id || practice._id}`}
          >
            <Bell size={compact ? 12 : 14} className="mr-1" weight="bold" /> Sollecita
          </Button>
          {solicitCooldown && (
            <div
              className="text-[10px] leading-tight text-slate-500 italic max-w-[180px]"
              data-testid={`solicit-cooldown-${practice.id || practice._id}`}
            >
              Ultimo sollecito: {solicitCooldown.lastDate} alle {solicitCooldown.lastTime}
            </div>
          )}
        </div>
        <SolicitDialog
          open={solicitOpen}
          onClose={() => setSolicitOpen(false)}
          practice={practice}
          onSent={onSent}
        />
      </div>
    );
  }

  if (completed && withinIntegrationWindow) {
    return (
      <>
        <Button
          size={compact ? "sm" : "default"}
          variant="outline"
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
          onClick={(e) => { e.stopPropagation(); onBeforeAction?.(); setIntegrationOpen(true); }}
          data-testid={`request-integration-btn-${practice.id || practice._id}`}
        >
          <EnvelopeSimple size={compact ? 12 : 14} className="mr-1" weight="bold" /> Richiedi integrazioni
        </Button>
        <IntegrationDialog
          open={integrationOpen}
          onClose={() => setIntegrationOpen(false)}
          practice={practice}
          onSent={onSent}
        />
      </>
    );
  }

  return null;
}

/** Modal: sollecito operatore in carico */
function SolicitDialog({ open, onClose, practice, onSent }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const pid = practice.id || practice._id;
  useEffect(() => { if (!open) setMessage(""); }, [open]);

  const submit = async () => {
    if (!message.trim()) { toast.error("Scrivi il messaggio"); return; }
    setSending(true);
    try {
      const r = await api.post(`/practices/${pid}/solicit`, { message: message.trim() });
      if (r.data?.email_sent) {
        toast.success("Sollecito inviato all'operatore. Riceverai la risposta via email.");
      } else if (r.data?.email_error) {
        toast.warning(`Salvato ma email non partita: ${r.data.email_error}`);
      } else {
        toast.success("Sollecito registrato");
      }
      onSent && onSent();
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio sollecito");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!sending && !v) onClose && onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="solicit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell size={18} weight="bold" /> Sollecita l'operatore
          </DialogTitle>
          <DialogDescription>
            Scrivi un messaggio per l'operatore che sta lavorando alla pratica{" "}
            <strong className="text-slate-800">{practice.service_name}</strong>.
            Riceverai la sua risposta direttamente sulla tua email.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Textarea
            rows={6}
            value={message}
            maxLength={3000}
            onChange={(e) => setMessage(e.target.value.slice(0, 3000))}
            placeholder="Es. Buongiorno, potete darmi un aggiornamento sullo stato di lavorazione?"
            data-testid="solicit-message-input"
          />
          <div className="text-[10px] text-slate-500 mt-1">{message.length}/3000</div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending}>Annulla</Button>
          <Button
            className="ht-gradient border-0 text-white"
            onClick={submit}
            disabled={sending || !message.trim()}
            data-testid="solicit-submit"
          >
            {sending ? "Invio…" : (<><PaperPlaneTilt size={14} className="mr-2" weight="bold" /> Invia sollecito</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Modal: richiesta integrazioni post-consegna */
function IntegrationDialog({ open, onClose, practice, onSent }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const pid = practice.id || practice._id;
  useEffect(() => { if (!open) setMessage(""); }, [open]);

  const submit = async () => {
    if (!message.trim()) { toast.error("Scrivi il messaggio"); return; }
    setSending(true);
    try {
      const r = await api.post(`/practices/${pid}/request-integration`, { message: message.trim() });
      if (r.data?.email_sent) {
        toast.success("Richiesta inviata all'operatore che ha consegnato. Riceverai la risposta via email.");
      } else if (r.data?.email_error) {
        toast.warning(`Salvato ma email non partita: ${r.data.email_error}`);
      } else {
        toast.success("Richiesta registrata");
      }
      onSent && onSent();
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio richiesta");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!sending && !v) onClose && onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="integration-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <EnvelopeSimple size={18} weight="bold" /> Richiedi integrazioni
          </DialogTitle>
          <DialogDescription>
            La finestra è aperta 48 ore lavorative dalla consegna. Scrivi cosa manca: la richiesta
            arriverà via email all'operatore che ha consegnato la pratica.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Textarea
            rows={6}
            value={message}
            maxLength={3000}
            onChange={(e) => setMessage(e.target.value.slice(0, 3000))}
            placeholder="Es. Mi manca la planimetria firmata digitalmente e il timbro sul primo foglio del PDF."
            data-testid="integration-message-input"
          />
          <div className="text-[10px] text-slate-500 mt-1">{message.length}/3000</div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={sending}>Annulla</Button>
          <Button
            className="ht-gradient border-0 text-white"
            onClick={submit}
            disabled={sending || !message.trim()}
            data-testid="integration-submit"
          >
            {sending ? "Invio…" : (<><PaperPlaneTilt size={14} className="mr-2" weight="bold" /> Invia richiesta</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Modal "Anteprima servizio" per il PRO — sostituisce la navigazione al dettaglio pratica.
 * Mostra: info principali, workflow con step attivo, storico se completata, satisfaction widget,
 * e il bottone azione contestuale (Sollecita / Richiedi integrazioni).
 */
export function PracticeQuickViewModal({ open, onClose, practiceRow }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const pid = practiceRow?.id || practiceRow?._id;

  const load = async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const r = await api.get(`/practices/${pid}/pro-quick-view`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento");
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && pid) load();
    else if (!open) setData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pid]);

  const merged = { ...(practiceRow || {}), ...(data || {}) };
  const statusMap = {
    completed: { label: "✓ Completato", cls: "bg-emerald-100 text-emerald-800" },
    cancelled: { label: "Annullato", cls: "bg-slate-200 text-slate-600" },
    awaiting_payment: { label: "⏱ Attesa pagamento", cls: "bg-yellow-100 text-yellow-800" },
  };
  const statusBadge = statusMap[merged.status] || {
    label: "⏳ In lavorazione", cls: "bg-amber-100 text-amber-800",
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pro-quick-view">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusBadge.cls}`}>
                  {statusBadge.label}
                </span>
                {merged.total_price != null && (
                  <span className="text-xs font-mono text-slate-500">{fmt(merged.total_price)}</span>
                )}
              </div>
              <DialogTitle className="text-xl">{merged.service_name}</DialogTitle>
              {merged.property_address && (
                <DialogDescription className="flex items-center gap-1 mt-1">
                  <MapPin size={12} /> {merged.property_address}{merged.property_city ? `, ${merged.property_city}` : ""}
                </DialogDescription>
              )}
              {/* Consegna: data e giorni calcolati dal backend (anchor = ultimo evento bloccante completato). */}
              {!["completed", "cancelled"].includes(merged.status) && Number(merged.delivery_time_days) > 0 && (() => {
                const visitIso = merged.visit_slot || merged.scheduled_visit_slot || merged.scheduled_appointment;
                const fmtD = (d) => d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
                const visit = visitIso ? new Date(visitIso) : null;
                const hasVisit = visit && !Number.isNaN(visit.getTime());
                if (!merged.delivery_deadline) {
                  return (
                    <div className="mt-2 text-xs" data-testid="preview-countdown-pending">
                      <div>
                        <span className="text-slate-500">Consegna: </span>
                        <span className="font-semibold text-slate-700">{Number(merged.delivery_time_days)} giorni dal completamento di pagamenti, firme e sopralluogo</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5" data-testid="preview-waiting-reasons">
                        In attesa{merged.waiting_reasons?.length > 0
                          ? <>: <span className="font-semibold text-amber-700">{merged.waiting_reasons.join(" · ")}</span></>
                          : ": il conteggio partirà quando saranno completati"}
                      </div>
                    </div>
                  );
                }
                const due = new Date(merged.delivery_deadline);
                return (
                  <div className="mt-2 text-xs" data-testid="preview-countdown">
                    <div>
                      <span className="text-slate-500">Consegna stimata: </span>
                      <span className="font-semibold text-emerald-700" data-testid="preview-delivery-date">entro il {fmtD(due)}</span>
                      {merged.days_left != null && (
                        <span className="ml-1 text-slate-500" data-testid="preview-days-left">
                          ({merged.days_left < 0 ? `in ritardo di ${-merged.days_left} gg` : merged.days_left === 0 ? "oggi" : `${merged.days_left} gg`})
                        </span>
                      )}
                    </div>
                    {hasVisit && (
                      <div className="text-[11px] text-blue-700 mt-0.5" data-testid="preview-visit-date">
                        <CalendarBlank size={11} className="inline mr-1" weight="bold" />
                        Sopralluogo: {fmtD(visit)}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="shrink-0">
              {/* Iter209bt — Rimosso `onBeforeAction={onClose}`: apriamo il
                  dialog di sollecito/integrazione SOPRA il modal della
                  pratica, senza chiuderlo. Il PracticeActionButton mostra
                  il proprio toast di conferma; l'`onSent` (load) aggiorna
                  la timeline del modal ancora aperto. */}
              <PracticeActionButton practice={merged} onSent={load} />
            </div>
          </div>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-sm text-slate-500">Caricamento…</div>
        )}

        {data && (
          <div className="space-y-5 py-2">
            {/* Workflow steps */}
            {(data.workflow_steps || []).length > 0 && (
              <div>
                <div className="label-eyebrow text-[10px] mb-2">Avanzamento</div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {data.workflow_steps.map((s, i, arr) => (
                    <React.Fragment key={s.id}>
                      <div className={`px-3 py-1.5 rounded-md text-xs font-medium ${
                        s.is_current ? "ht-gradient text-white" :
                        s.is_past ? "bg-emerald-100 text-emerald-800" :
                        "bg-slate-100 text-slate-500"
                      }`}>
                        {s.is_past && <CheckCircle size={11} weight="fill" className="inline mr-1" />}
                        {s.name}
                      </div>
                      {i < arr.length - 1 && (
                        <span className="text-slate-300 text-xs">→</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                {merged.status !== "completed" && merged.current_step_name && (
                  <div className="text-xs text-slate-600 mt-2">
                    Step corrente: <strong className="text-purple-700">{merged.current_step_name}</strong>
                  </div>
                )}
                {/* Iter207.18 — Link diretto alla cartella documenti dell'immobile
                    creata per questa pratica (visibile solo su pratiche completate). */}
                {merged.status === "completed" && merged.property_id && (
                  <div className="mt-3">
                    <Link
                      to={`/properties/${merged.property_id}?folder_name=${encodeURIComponent(merged.service_name || "")}`}
                      onClick={() => onClose && onClose()}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 hover:text-purple-900 hover:underline"
                      data-testid="quickview-goto-documents"
                    >
                      <FolderOpen size={16} weight="duotone" />
                      Vai ai documenti
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Iter207.18 — SatisfactionWidget rimosso dal marketplace modal.
                Ora appare direttamente nella cartella documenti dell'immobile
                (vedi PropertyDetail.jsx), più vicino ai file consegnati. */}

            {/* Iter208.87 — Azioni per il Pro (tasti custom configurati dall'admin)
                inserite tra AVANZAMENTO e STORICO, visibili solo al pro owner. */}
            <PracticeCustomActionsPanel practiceId={pid} onSubmitted={load} />

            {/* Storico avanzamento */}
            {(data.history || []).length > 0 && (
              <div>
                <div className="label-eyebrow text-[10px] mb-2">Storico</div>
                <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
                  {data.history.slice().reverse().map((h, i) => {
                    // Iter208.92 — stile distinto per i messaggi del pro
                    const isProMsg = h.action === "custom_action_submitted";
                    return (
                      <div
                        key={i}
                        className={
                          isProMsg
                            ? "flex items-start gap-2 text-xs border-l-4 border-purple-500 bg-purple-50 rounded-r px-2.5 py-1.5"
                            : "flex items-start gap-2 text-xs border-l-2 border-purple-200 pl-2 py-0.5"
                        }
                        data-testid={isProMsg ? `history-pro-message-${i}` : undefined}
                      >
                        {isProMsg ? (
                          <ChatCircleText size={13} weight="fill" className="text-purple-600 mt-0.5 shrink-0" />
                        ) : (
                          <Clock size={11} className="text-slate-400 mt-0.5 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          {isProMsg ? (
                            <>
                              <div className="font-semibold text-purple-900">
                                Messaggio del pro
                                <span className="ml-1 font-normal text-slate-600">— «{h.action_name || "azione"}»</span>
                              </div>
                              {Array.isArray(h.answers) && h.answers.length > 0 && (
                                <div className="text-slate-700 mt-0.5 whitespace-pre-line">
                                  {h.answers
                                    .filter((a) => a.value != null && a.value !== "")
                                    .map((a) => `${a.label || a.block_id}: ${typeof a.value === "object" ? JSON.stringify(a.value) : String(a.value)}`)
                                    .join("\n")
                                    .slice(0, 400)}
                                </div>
                              )}
                              <div className="text-[10px] text-slate-500 mt-0.5">{fmtDate(h.at)}</div>
                            </>
                          ) : (
                            <>
                              <div className="font-medium capitalize">{humanAction(h.action)}</div>
                              {h.step && <div className="text-slate-500">→ {h.step}</div>}
                              {h.note && <div className="text-slate-600 mt-0.5 italic">"{h.note}"</div>}
                              <div className="text-[10px] text-slate-400">{fmtDate(h.at)}</div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Iter209bu — Servizi concatenati del visit group */}
            {(data.chained_services || []).length > 0 && (
              <div data-testid="chained-services-section">
                <div className="label-eyebrow text-[10px] mb-2 flex items-center gap-1.5">
                  <Link2 size={12} weight="bold" /> Servizi concatenati ({data.chained_services.length})
                </div>
                <div className="space-y-1.5">
                  {data.chained_services.map((c) => {
                    const csMap = {
                      completed: { label: "✓ Completato", cls: "bg-emerald-100 text-emerald-800" },
                      cancelled: { label: "Annullato", cls: "bg-slate-200 text-slate-600" },
                    };
                    const b = csMap[c.status] || { label: "⏳ In lavorazione", cls: "bg-amber-100 text-amber-800" };
                    return (
                      <div key={c.id}
                        className="flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50/40 px-2.5 py-1.5 text-xs"
                        data-testid={`chained-service-${c.id}`}>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${b.cls}`}>{b.label}</span>
                        <span className="flex-1 font-medium truncate">{c.service_name}</span>
                        {c.current_step_name && (
                          <span className="text-slate-500 truncate">Step: {c.current_step_name}</span>
                        )}
                        {c.total_price != null && (
                          <span className="font-mono text-slate-500">{fmt(c.total_price)}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Iter208.84 — Comunicazioni tra Housetag e il pro
                Iter209bu — aggregate su tutto il visit group se presente */}
            <CommunicationsSection
              practiceId={pid}
              practiceIds={data.group_practice_ids || [pid]}
            />

            {/* Nota finestra 48h */}
            {merged.status === "completed" && data.can_request_integration && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
                ⏰ Puoi ancora richiedere integrazioni per{" "}
                <strong>{Math.max(0, Math.round(data.window_hours - (data.hours_since_delivery || 0)))}h</strong> lavorative.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function humanAction(a) {
  const map = {
    created: "Creata",
    advanced: "Avanzata",
    delivered: "Consegnata",
    revoked: "Annullata",
    assigned: "Assegnata",
    reassigned: "Riassegnata",
    documents_requested: "Documenti richiesti",
    solicited: "Sollecito inviato",
    integration_requested_by_pro: "Integrazione richiesta",
    custom_action_submitted: "Messaggio del pro",
  };
  return map[a] || a;
}


/**
 * Iter208.84 — Log Comunicazioni tra Housetag e il professionista.
 * Mostra le voci automatiche generate da eventi (sollecito, cambio stato,
 * pagamento, ecc.) in ordine cronologico inverso. Nessun input testo libero.
 */
const COMM_MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
function fmtCommDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const day = d.getDate();
  const mon = COMM_MONTHS[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${mon} ${year}, ${hh}:${mm}`;
}

export function CommunicationsSection({ practiceId, practiceIds }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Iter209bu — Se `practiceIds` è valorizzato (visit group), aggrega le
  // comunicazioni di tutte le pratiche della catena e le ordina per data.
  const ids = practiceIds && practiceIds.length ? practiceIds : (practiceId ? [practiceId] : []);
  const idsKey = ids.join(",");

  useEffect(() => {
    let alive = true;
    if (ids.length === 0) return;
    setLoading(true);
    Promise.all(
      ids.map((id) =>
        api
          .get(`/communications?practice_id=${encodeURIComponent(id)}`)
          .then((r) => (r.data?.items || []).map((it) => ({ ...it, _origin_practice_id: id })))
          .catch(() => [])
      )
    )
      .then((results) => {
        if (!alive) return;
        const all = results.flat();
        all.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        setItems(all);
      })
      .catch((e) => {
        if (alive) setError(e.response?.data?.detail || "Errore caricamento comunicazioni");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return (
    <div data-testid="communications-section">
      <div className="label-eyebrow text-[10px] mb-2 flex items-center gap-1.5">
        <ChatCircleText size={12} weight="bold" /> Comunicazioni
      </div>
      {loading && <div className="text-xs text-slate-500 italic">Caricamento…</div>}
      {!loading && error && (
        <div className="text-xs text-red-600" data-testid="communications-error">{error}</div>
      )}
      {!loading && !error && items.length === 0 && (
        <div className="text-xs text-slate-500 italic" data-testid="communications-empty">
          Nessuna comunicazione registrata per questa pratica.
        </div>
      )}
      {!loading && !error && items.length > 0 && (
        <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1" data-testid="communications-list">
          {items.map((c) => {
            const isSystem = c.author_type === "system";
            const isOperator = c.author_type === "operator";
            const borderCls = isSystem ? "border-slate-200" : isOperator ? "border-blue-300" : "border-purple-300";
            const Icon = isSystem ? Info : ChatCircleText;
            return (
              <div
                key={c.id}
                className={`flex items-start gap-2 text-xs border-l-2 ${borderCls} pl-2 py-0.5`}
                data-testid={`comm-item-${c.id}`}
              >
                <Icon size={11} className="text-slate-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className={isSystem ? "text-slate-600 italic" : "text-slate-800"}>
                    <span className="font-semibold not-italic">{c.author_name}</span>{" "}
                    <span>{c.content}</span>
                    {c.subject && (
                      <span className="text-slate-400 not-italic"> · {c.subject}</span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{fmtCommDate(c.timestamp)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/**
 * Iter208.87 — Bottone inline nella colonna "AZIONE" della lista pratiche
 * lato pro. Legge le azioni custom applicabili allo step corrente della
 * pratica e, se ce n'è almeno una, mostra il primo bottone; al click
 * apre direttamente il form (senza passare dal quickview).
 *
 * Se ci sono più azioni, mostra un menu dropdown con le opzioni.
 */
export function PracticeCustomActionInlineButton({ practice }) {
  const pid = practice?.id || practice?._id;
  const [actions, setActions] = useState([]);
  const [activeAction, setActiveAction] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const refresh = React.useCallback(() => {
    if (!pid) return;
    api.get(`/practices/${pid}/custom-actions`)
      .then((r) => setActions(r.data?.actions || []))
      .catch(() => setActions([]));
  }, [pid]);

  useEffect(() => {
    let alive = true;
    if (!pid) return;
    api.get(`/practices/${pid}/custom-actions`)
      .then((r) => { if (alive) setActions(r.data?.actions || []); })
      .catch(() => { if (alive) setActions([]); });
    return () => { alive = false; };
  }, [pid]);

  if (!actions.length) return null;

  const hasNew = actions.some((a) => a.is_new);
  const newIds = actions.filter((a) => a.is_new).map((a) => a.id);

  const trigger = async (a) => {
    setMenuOpen(false);
    setActiveAction(a);
    // Iter208.88 — Segna come viste tutte le azioni correntemente applicabili
    // così il badge scompare non appena il pro apre il form.
    if (hasNew) {
      try {
        await api.post(`/practices/${pid}/custom-actions/seen`, { action_ids: newIds });
        refresh();
      } catch { /* silenzioso: badge migliorabile alla prossima load */ }
    }
  };

  const only = actions.length === 1 ? actions[0] : null;

  return (
    <>
      {only ? (
        <button
          type="button"
          onClick={() => trigger(only)}
          className="relative inline-flex items-center gap-1 rounded-md border border-purple-300 bg-white text-purple-700 hover:bg-purple-50 px-2 py-1 text-[11px] font-semibold shrink-0"
          data-testid={`recap-custom-action-${pid}`}
          title={only.name || "Azione"}
        >
          <PaperPlaneTilt size={11} weight="bold" />
          {only.name || "Azione"}
          {hasNew && (
            <span
              className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white animate-pulse"
              data-testid={`recap-custom-action-new-${pid}`}
              aria-label="Nuova azione disponibile"
            />
          )}
        </button>
      ) : (
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="relative inline-flex items-center gap-1 rounded-md border border-purple-300 bg-white text-purple-700 hover:bg-purple-50 px-2 py-1 text-[11px] font-semibold shrink-0"
            data-testid={`recap-custom-action-${pid}`}
          >
            <PaperPlaneTilt size={11} weight="bold" /> Azioni ({actions.length})
            {hasNew && (
              <span
                className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white animate-pulse"
                data-testid={`recap-custom-action-new-${pid}`}
                aria-label="Nuova azione disponibile"
              />
            )}
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 z-30 min-w-[180px] rounded-md border border-slate-200 bg-white shadow-lg py-1">
              {actions.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => trigger(a)}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-purple-50 hover:text-purple-700 flex items-center justify-between"
                  data-testid={`recap-custom-action-item-${pid}-${a.id}`}
                >
                  <span>{a.name || "Azione"}</span>
                  {a.is_new && (
                    <span className="w-2 h-2 bg-red-500 rounded-full shrink-0" aria-label="nuova" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {activeAction && (
        <InlineCustomActionModal
          action={activeAction}
          practiceId={pid}
          onClose={() => setActiveAction(null)}
        />
      )}
    </>
  );
}


/**
 * Iter208.87 — Modal minimale per il form di un'azione custom, riutilizzato
 * quando l'azione viene aperta direttamente dalla colonna "AZIONE" della
 * lista (senza aprire il quickview della pratica).
 */
function InlineCustomActionModal({ action, practiceId, onClose }) {
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const setAns = (bid, val) => setAnswers((a) => ({ ...a, [bid]: val }));

  const submit = async () => {
    for (const b of action.blocks || []) {
      if (b.required && !answers[b.id]) {
        toast.warning(`Compila "${b.label || 'campo'}" per continuare`);
        return;
      }
      if (b.required && b.type === "file_upload" && (!Array.isArray(answers[b.id]) || answers[b.id].length === 0)) {
        toast.warning(`Allega almeno un file per "${b.label || 'file'}"`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/practices/${practiceId}/custom-actions/${action.id}/submit`, { answers });
      setConfirmation(r.data?.confirmation_text || "Richiesta inviata!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="recap-custom-action-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PaperPlaneTilt size={18} weight="bold" className="text-purple-600" />
            {action.name || "Azione"}
          </DialogTitle>
          {!confirmation && (
            <DialogDescription>
              Compila i campi qui sotto e premi <strong>Invia</strong>. La richiesta arriverà
              agli operatori assegnati allo step.
            </DialogDescription>
          )}
        </DialogHeader>
        {confirmation ? (
          <div className="py-6 text-center space-y-3" data-testid="recap-custom-action-success">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle size={28} weight="fill" />
            </div>
            <div className="text-slate-800 font-medium whitespace-pre-line">{confirmation}</div>
            <Button onClick={onClose} className="bg-purple-600 hover:bg-purple-700 text-white">Chiudi</Button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {(action.blocks || []).map((b) => (
                <InlineBlockRenderer
                  key={b.id}
                  block={b}
                  value={answers[b.id]}
                  onChange={(v) => setAns(b.id, v)}
                  practiceId={practiceId}
                  actionId={action.id}
                />
              ))}
              {(!action.blocks || action.blocks.length === 0) && (
                <div className="text-xs italic text-slate-500 text-center py-4">
                  Nessun campo da compilare — premi Invia per notificare l'operatore.
                </div>
              )}
            </div>
            <DialogFooter className="pt-3 border-t mt-3">
              <Button variant="ghost" onClick={onClose} disabled={submitting}>Annulla</Button>
              <Button
                onClick={submit}
                disabled={submitting}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="recap-custom-action-submit"
              >
                {submitting ? "Invio…" : (<><PaperPlaneTilt size={14} weight="bold" className="mr-1" />Invia</>)}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Wrapper minimale che riusa i tre tipi di blocco (textarea/calendar_slots/file_upload).
// Delega al componente esistente il rendering del blocco `file_upload` (upload verso backend).
function InlineBlockRenderer({ block, value, onChange, practiceId, actionId }) {
  if (block.type === "textarea") {
    return (
      <div>
        <label className="text-xs font-semibold text-slate-700 mb-1 block">
          {block.label || "Note"} {block.required && <span className="text-red-500">*</span>}
        </label>
        <Textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  if (block.type === "calendar_slots") {
    return (
      <div>
        <label className="text-xs font-semibold text-slate-700 mb-1 block">
          <CalendarBlank size={12} weight="bold" className="inline mr-1 text-purple-600" />
          {block.label || "Disponibilità"} {block.required && <span className="text-red-500">*</span>}
        </label>
        {(!block.slots || block.slots.length === 0) ? (
          <div className="text-xs italic text-slate-500 border rounded p-3">Nessuno slot configurato.</div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {block.slots.map((s, i) => {
              const active = value && value.date === s.date && value.start === s.start && value.end === s.end;
              const label = `${new Date(s.date).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })} · ${s.start}–${s.end}`;
              return (
                <button
                  key={`${s.date}-${s.start}-${i}`}
                  type="button"
                  onClick={() => onChange({ date: s.date, start: s.start, end: s.end })}
                  className={
                    "text-sm px-3 py-2 rounded-full border transition-all " +
                    (active
                      ? "bg-purple-600 border-purple-600 text-white shadow-sm"
                      : "bg-white border-slate-200 text-slate-700 hover:border-purple-400 hover:text-purple-700")
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }
  if (block.type === "file_upload") {
    // Iter209r — Mostra il messaggio "Questa azione richiede allegati…"
    // SOLO se il blocco ha `required: true` e nessun file è già stato
    // caricato. Se lo step non richiede allegati (o sono già presenti
    // in `value`), non renderizziamo nulla: il modal inline resta
    // pulito e non induce il pro a cercare un upload che non serve.
    // Il caricamento reale dei file avviene sempre dalla scheda pratica
    // completa (rara nel caso azione inline, cfr. FileUploadBlock in
    // PracticeCustomActionsPanel).
    const alreadyUploaded = Array.isArray(value) && value.length > 0;
    if (!block.required || alreadyUploaded) return null;
    return (
      <div
        className="text-xs italic text-slate-500 border rounded p-3"
        data-testid={`inline-file-upload-hint-${block.id}`}
      >
        Questa azione richiede allegati: apri la scheda pratica per caricarli.
      </div>
    );
  }
  return null;
}
